import React, { useState, useEffect } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import {
  ActivityCatalogEntry,
  ActivityLevelInput,
  IntensityLevel,
} from '../services/activityService';

interface ActivityCatalogFormModalProps {
  open: boolean;
  entry?: ActivityCatalogEntry;
  onSave: (levels: Partial<Record<IntensityLevel, ActivityLevelInput>>) => Promise<void>;
  onCancel: () => void;
}

const LEVELS: { key: IntensityLevel; label: string }[] = [
  { key: 'low', label: 'Bajo' },
  { key: 'mid', label: 'Medio' },
  { key: 'high', label: 'Alto' },
];

interface LevelFormState {
  kcalPerKgMin: string;
  carbsPct: string; // shown as a percentage (0-100)
}

type FormState = Record<IntensityLevel, LevelFormState>;

const EMPTY_LEVEL: LevelFormState = { kcalPerKgMin: '', carbsPct: '' };

const toFormState = (entry?: ActivityCatalogEntry): FormState => {
  const build = (level: IntensityLevel): LevelFormState => {
    const l = entry?.levels[level];
    if (!l) return { ...EMPTY_LEVEL };
    return {
      kcalPerKgMin: String(l.kcalPerKgMin),
      carbsPct: String(Math.round(l.carbsPct * 100)),
    };
  };
  return { low: build('low'), mid: build('mid'), high: build('high') };
};

const ActivityCatalogFormModal: React.FC<ActivityCatalogFormModalProps> = ({ open, entry, onSave, onCancel }) => {
  const [form, setForm] = useState<FormState>(toFormState());
  const [errors, setErrors] = useState<Partial<Record<IntensityLevel, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const isMobile = useIsMobile();

  useEffect(() => {
    if (open) {
      setForm(toFormState(entry));
      setErrors({});
      setSubmitError('');
    }
  }, [open, entry]);

  const handleChange = (level: IntensityLevel, field: keyof LevelFormState, value: string) => {
    setForm(prev => ({ ...prev, [level]: { ...prev[level], [field]: value } }));
    if (errors[level]) setErrors(prev => ({ ...prev, [level]: '' }));
    setSubmitError('');
  };

  const buildPayload = (): { levels?: Partial<Record<IntensityLevel, ActivityLevelInput>>; errors: Partial<Record<IntensityLevel, string>> } => {
    const levels: Partial<Record<IntensityLevel, ActivityLevelInput>> = {};
    const newErrors: Partial<Record<IntensityLevel, string>> = {};

    LEVELS.forEach(({ key }) => {
      const { kcalPerKgMin, carbsPct } = form[key];
      const kcalFilled = kcalPerKgMin.trim() !== '';
      const carbsFilled = carbsPct.trim() !== '';
      if (!kcalFilled && !carbsFilled) return; // level left unconfigured

      if (!kcalFilled || !carbsFilled) {
        newErrors[key] = 'Completa ambos campos';
        return;
      }
      const kcal = Number(kcalPerKgMin);
      const carbs = Number(carbsPct);
      if (isNaN(kcal) || kcal < 0) {
        newErrors[key] = 'Kcal inválido';
        return;
      }
      if (isNaN(carbs) || carbs < 0 || carbs > 100) {
        newErrors[key] = 'Carbohidratos: 0-100%';
        return;
      }
      levels[key] = { kcalPerKgMin: kcal, carbsPct: carbs / 100 };
    });

    return { levels, errors: newErrors };
  };

  const handleSave = async () => {
    const { levels, errors: newErrors } = buildPayload();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    if (!levels || Object.keys(levels).length === 0) {
      setSubmitError('Configura al menos un nivel.');
      return;
    }
    setIsSubmitting(true);
    setSubmitError('');
    try {
      await onSave(levels);
    } catch (error: any) {
      setSubmitError(error.response?.error || error.message || 'Error al guardar. Por favor intente nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  const labelStyle: React.CSSProperties = { fontWeight: 600, marginBottom: 6, display: 'block', fontSize: 13, color: '#374151' };
  const inputStyle = (hasError: boolean): React.CSSProperties => ({
    width: '100%', padding: '10px 12px',
    border: `1px solid ${hasError ? '#dc2626' : '#cbd5e1'}`,
    borderRadius: 6, fontSize: 14, outline: 'none', boxSizing: 'border-box',
  });

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
      background: 'rgba(0,0,0,0.18)', display: 'flex', alignItems: 'center',
      justifyContent: 'center', zIndex: 1100, padding: isMobile ? '12px' : '20px',
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, padding: isMobile ? '24px 20px' : '32px 40px',
        width: '100%', maxWidth: 560, maxHeight: '90vh', overflow: 'auto',
        boxShadow: '0 4px 32px rgba(0,0,0,0.13)',
      }}>
        <h2 style={{ marginTop: 0, marginBottom: 6, fontSize: 22, fontWeight: 600, color: '#1f2937' }}>
          Configurar niveles
        </h2>
        <p style={{ margin: '0 0 20px 0', fontSize: 13, color: '#6b7280' }}>
          {entry?.activityType.name
            ? <>Define los niveles de intensidad para <strong>{entry.activityType.name}</strong>. Deja un nivel vacío para no configurarlo.</>
            : 'Define los niveles de intensidad.'}
        </p>

        {submitError && (
          <div style={{ background: '#fee2e2', color: '#dc2626', padding: '12px 16px', borderRadius: 8, marginBottom: 20, fontSize: 14 }}>
            {submitError}
          </div>
        )}

        {LEVELS.map(({ key, label }) => (
          <div key={key} style={{ marginBottom: 18 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#1f2937', marginBottom: 8 }}>{label}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <label style={{ display: 'block' }}>
                <span style={labelStyle}>Kcal por kg·min</span>
                <input
                  type="number" min="0" step="any"
                  value={form[key].kcalPerKgMin}
                  onChange={e => handleChange(key, 'kcalPerKgMin', e.target.value)}
                  style={inputStyle(!!errors[key])}
                  placeholder="0.08"
                />
              </label>
              <label style={{ display: 'block' }}>
                <span style={labelStyle}>Carbohidratos (%)</span>
                <input
                  type="number" min="0" max="100" step="any"
                  value={form[key].carbsPct}
                  onChange={e => handleChange(key, 'carbsPct', e.target.value)}
                  style={inputStyle(!!errors[key])}
                  placeholder="50"
                />
              </label>
            </div>
            {errors[key] && <span style={{ color: '#dc2626', fontSize: 12, marginTop: 6, display: 'block' }}>{errors[key]}</span>}
          </div>
        ))}

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', paddingTop: 12 }}>
          <button
            onClick={onCancel}
            disabled={isSubmitting}
            style={{
              background: '#f3f4f6', border: 'none', borderRadius: 6, padding: '11px 24px',
              fontWeight: 600, fontSize: 15, cursor: isSubmitting ? 'not-allowed' : 'pointer',
              opacity: isSubmitting ? 0.6 : 1, color: '#374151',
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={isSubmitting}
            style={{
              background: isSubmitting ? '#81c9c4' : '#38b2ac', color: '#fff', border: 'none',
              borderRadius: 6, padding: '11px 24px', fontWeight: 600, fontSize: 15,
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
            }}
          >
            {isSubmitting ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActivityCatalogFormModal;
